import {
  Collapse,
  CollapseProps,
  Divider,
  List,
  Space,
  Switch,
  Typography,
} from "antd";
import { QuestionsSiderListItem } from "./QuestionsSiderListItem";
import { useExamStore } from "@store";
import { useTranslation } from "react-i18next";
import { ExamInstanceTree, ExamQuestionType } from "@types";
import { useEffect, useState } from "react";

interface Props {
  examInstanceTree?: ExamInstanceTree
}

// Question types that require manual grading
const MANUAL_GRADING_TYPES = [
  ExamQuestionType.EssayQuestion,
  ExamQuestionType.ShortAnswer,
];

export const QuestionsSider = ({examInstanceTree}: Props) => {
  const clickedQuestion = useExamStore((state) => {
    return examInstanceTree?.groups[state.clickedGroup].questions[state.clickedQuestionIndex];
  });
  const setClickedIndex = useExamStore((state) => state.setQuestionIndex);
  const [activeKeys, setActiveKeys] = useState<string[]>();
  const [showOnlyEssay, setShowOnlyEssay] = useState<boolean>(true);
  const theme = useExamStore((state) => state.theme);

  const {t} = useTranslation();

  // Filter questions based on showOnlyEssay toggle
  const getFilteredQuestions = (questions: ExamInstanceTree['groups'][0]['questions']) => {
    if (!showOnlyEssay) return questions;
    return questions.filter(q => MANUAL_GRADING_TYPES.includes(q.type));
  };

  // Check if a group has any essay/short answer questions
  const groupHasManualGradingQuestions = (questions: ExamInstanceTree['groups'][0]['questions']) => {
    return questions.some(q => MANUAL_GRADING_TYPES.includes(q.type));
  };

  useEffect(() => {
    if (examInstanceTree) {
      // Debug: log question types to see what's coming from API
      console.log('=== DEBUG: Question types ===');
      console.log('MANUAL_GRADING_TYPES:', MANUAL_GRADING_TYPES);
      examInstanceTree.groups.forEach((group, gIdx) => {
        group.questions.forEach((q, qIdx) => {
          console.log(`Group ${gIdx}, Question ${qIdx}: type="${q.type}", isManual=${MANUAL_GRADING_TYPES.includes(q.type)}`);
        });
      });
      
      setActiveKeys(examInstanceTree?.groups?.map((_, index: number) => index.toString()))
      
      // When showOnlyEssay is true, find the first group with manual grading questions
      // and set the initial question index to the first manual grading question in that group
      if (showOnlyEssay) {
        const sortedGroups = [...examInstanceTree.groups]
          .map((group, originalIndex) => ({ group, originalIndex }))
          .sort((a, b) => a.group.orderNumber - b.group.orderNumber);
        
        for (const { group, originalIndex } of sortedGroups) {
          if (groupHasManualGradingQuestions(group.questions)) {
            const firstManualQuestion = group.questions.findIndex(q => 
              MANUAL_GRADING_TYPES.includes(q.type)
            );
            if (firstManualQuestion >= 0) {
              setClickedIndex(firstManualQuestion, originalIndex);
              break;
            }
          }
        }
      } else {
        // When showing all questions, select the first question of the first group
        const sortedGroups = [...examInstanceTree.groups]
          .map((group, originalIndex) => ({ group, originalIndex }))
          .sort((a, b) => a.group.orderNumber - b.group.orderNumber);
        
        if (sortedGroups.length > 0 && sortedGroups[0].group.questions.length > 0) {
          setClickedIndex(0, sortedGroups[0].originalIndex);
        }
      }
    }
  }, [examInstanceTree, showOnlyEssay]);

  const handleCollapseChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  // Sort groups once and keep track of original indices
  const sortedGroupsWithIndices = examInstanceTree?.groups
    ?.map((group, originalIndex) => ({ group, originalIndex }))
    .sort((a, b) => a.group.orderNumber - b.group.orderNumber) ?? [];

  const items: CollapseProps["items"] = sortedGroupsWithIndices
    .filter(({ group }) => !showOnlyEssay || groupHasManualGradingQuestions(group.questions))
    .map(({ group, originalIndex: originalGroupIndex }, displayIndex) => {
      const filteredQuestions = getFilteredQuestions(group.questions);
      
      return {
        key: displayIndex.toString(),
        label: (
          <Space style={{ display: "flex" }} direction="vertical">
            <Typography.Text strong>
              {originalGroupIndex + 1}. {group.name}
            </Typography.Text>
            <Divider style={{ margin: 0, backgroundColor: "#F1B80E" }} />
          </Space>
        ),
        children: (
          <List
            dataSource={filteredQuestions}
            split={false}
            renderItem={(item) => {
              // Find original index in the unfiltered questions array
              const originalQuestionIndex = group.questions.findIndex(q => q.id === item.id);
              return (
                <QuestionsSiderListItem
                  clickedQuestion={clickedQuestion}
                  index={originalQuestionIndex}
                  question={item}
                  onClick={() => {
                    setClickedIndex(originalQuestionIndex, originalGroupIndex);   
                  }}
                />
              );
            }}
          />
        ),
      };
    });


  return (
    <Space direction="vertical" style={{ display: "flex" }}>
      <div
        style={{
          padding: "15px 24px",
          backgroundColor: theme === "dark" ? "#141414" : "#fdf6e2",
          borderRadius: 6,
        }}
      >
        <Typography.Title style={{ margin: 0 }} level={4}>
          {t("pages.examPeriods.questions")}
        </Typography.Title>
      </div>
      <div
        style={{
          padding: "8px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: theme === "dark" ? "#1f1f1f" : "#fafafa",
          borderRadius: 6,
          border: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
        }}
      >
        <Typography.Text style={{ fontSize: 13 }}>
          {t("pages.examPeriods.showAllQuestions")}
        </Typography.Text>
        <Switch
          size="small"
          checked={!showOnlyEssay}
          onChange={(checked) => setShowOnlyEssay(!checked)}
        />
      </div>
      <Collapse
        style={{ overflow: "auto", height: "calc(100vh - 280px)" }}
        ghost
        items={items}          
        onChange={handleCollapseChange}
        activeKey={activeKeys}
      />
    </Space>
  );
};
